import React, { useState, useRef, useEffect } from "react";
import { MessageSquare, Linkedin, Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { personalInfo } from "../data/mock";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { submitContact } from "../api/client";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const onChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Isi semua field yang wajib."); return; }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Pesan terkirim!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setDone(false), 5000);
    } catch { toast.error("Gagal mengirim. Coba lagi."); }
    finally { setSubmitting(false); }
  };

  const inp = { background: "var(--bg)", border: "1px solid rgba(255,255,255,0.08)", color: "#f2f2ff", fontFamily: "'DM Sans', sans-serif", fontSize: 14, borderRadius: 3 };
  const lbl = { fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 500, color: "#6a6a88", marginBottom: 8, display: "block", letterSpacing: 1 };

  return (
    <div style={{ padding: "120px 0", background: "var(--surface)", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>

        {/* Big headline — editorial */}
        <div ref={ref} className="reveal" style={{ marginBottom: 80, position: "relative" }}>
          <div className="ghost-number" style={{ position: "absolute", left: -20, top: -60, zIndex: 0 }}>05</div>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 3, marginBottom: 20 }}>KONTAK</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(36px, 7vw, 96px)", fontWeight: 700, color: "#f2f2ff", margin: "0 0 8px", letterSpacing: "-3px", lineHeight: 0.95 }}>
              MARI KERJA<br />
              <span style={{ background: "linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)", backgroundSize: "400% 400%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "holo-shift 5s ease infinite" }}>SAMA.</span>
            </h2>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.8fr", gap: 80, alignItems: "start" }} className="contact-grid">

          {/* Left info */}
          <div>
            {[
              { icon: <Mail size={16} />, label: "EMAIL", value: personalInfo.email, href: `mailto:${personalInfo.email}` },
              { icon: <Phone size={16} />, label: "TELEPON", value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
              { icon: <MapPin size={16} />, label: "LOKASI", value: personalInfo.location },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 16, marginBottom: 28 }}>
                <div style={{ width: 40, height: 40, borderRadius: 3, background: "var(--card)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00e5ff", flexShrink: 0 }}>{item.icon}</div>
                <div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6a6a88", letterSpacing: 2, marginBottom: 4 }}>{item.label}</div>
                  {item.href
                    ? <a href={item.href} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#c0c0d0", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={e => e.target.style.color = "#00e5ff"} onMouseLeave={e => e.target.style.color = "#c0c0d0"}>{item.value}</a>
                    : <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#c0c0d0" }}>{item.value}</span>}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
              <a href={`https://wa.me/${personalInfo.whatsapp}?text=${encodeURIComponent("Halo Erwin, saya ingin berkonsultasi.")}`}
                target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "12px", borderRadius: 3, background: "#25D366", color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 13, textDecoration: "none", letterSpacing: 1, transition: "opacity 0.2s, transform 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
              >
                <MessageSquare size={16} /> WHATSAPP
              </a>
              <a href={personalInfo.linkedin} target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "12px", borderRadius: 3, background: "transparent", color: "#f2f2ff", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 13, textDecoration: "none", border: "1px solid rgba(255,255,255,0.1)", letterSpacing: 1, transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#0A66C2"; e.currentTarget.style.background = "rgba(10,102,194,0.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.background = "transparent"; }}
              >
                <Linkedin size={16} /> LINKEDIN
              </a>
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "var(--card)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 4, padding: 40, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, #00e5ff, #9b00ff, #ff0099, transparent)" }} />

            {done ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 60, height: 60, borderRadius: 3, background: "linear-gradient(135deg,#00e5ff,#9b00ff)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <CheckCircle2 size={26} color="#07070f" strokeWidth={2.5} />
                </div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 20, color: "#f2f2ff", marginBottom: 8 }}>Pesan Terkirim!</div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#6a6a88" }}>Terima kasih! Saya akan segera menghubungi Anda.</div>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div><Label style={lbl}>NAMA *</Label><Input name="name" value={form.name} onChange={onChange} placeholder="Nama Anda" style={inp} /></div>
                  <div><Label style={lbl}>EMAIL *</Label><Input name="email" type="email" value={form.email} onChange={onChange} placeholder="email@anda.com" style={inp} /></div>
                  <div><Label style={lbl}>TELEPON</Label><Input name="phone" value={form.phone} onChange={onChange} placeholder="+62 812..." style={inp} /></div>
                  <div><Label style={lbl}>SUBJEK</Label><Input name="subject" value={form.subject} onChange={onChange} placeholder="Kebutuhan IT Anda" style={inp} /></div>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <Label style={lbl}>PESAN *</Label>
                  <Textarea name="message" value={form.message} onChange={onChange} rows={5} placeholder="Ceritakan kebutuhan IT Anda..." style={{ ...inp, resize: "vertical" }} />
                </div>
                <button type="submit" disabled={submitting}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "14px", borderRadius: 3, background: submitting ? "rgba(0,229,255,0.2)" : "linear-gradient(135deg,#00e5ff,#9b00ff)", color: submitting ? "#6a6a88" : "#07070f", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14, border: "none", cursor: submitting ? "not-allowed" : "none", letterSpacing: 1, transition: "opacity 0.2s, transform 0.2s" }}
                  onMouseEnter={e => { if (!submitting) { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                  onMouseLeave={e => { if (!submitting) { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; } }}
                >
                  {submitting ? <div style={{ width: 16, height: 16, border: "2px solid transparent", borderTopColor: "#6a6a88", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> : <Send size={16} />}
                  {submitting ? "MENGIRIM..." : "KIRIM PESAN"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
